import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useRaffleTicketHolderLookupMutation } from "Api/Mutations/Raffle/useRaffleTicketHolderLookupMutation";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { RaffleRoundDto } from "Infrastructure/Api/Api";
import { getApiErrorStatus } from "Infrastructure/Api/ApiUtils";
import {
  getRaffleHolderLookupSchema,
  type RaffleHolderLookupFormData,
} from "Schemas/raffleHolderLookupSchema";
import { useForm } from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.Raffle.HolderLookup;

type Props = {
  isOpen: boolean;
  round: RaffleRoundDto | null;
  onClose: () => void;
};

type ContentProps = {
  round: RaffleRoundDto;
  onClose: () => void;
};

type LookupFailure = "notFound" | "sealMismatch" | "error";

type LookupFailureView = {
  severity: "warning" | "error";
  message: string;
};

// 400/404: the number does not resolve. 409 (the lookup is offered only on Closed/Drawn): the
// allocation fails its seal. Anything else is a failed call and must not read as "no such number".
const STATUS_TO_LOOKUP_FAILURE: Partial<Record<number, LookupFailure>> = {
  400: "notFound",
  404: "notFound",
  409: "sealMismatch",
};

const getLookupFailure = (error: unknown): LookupFailure => {
  const status = getApiErrorStatus(error);
  if (status === undefined) {
    return "error";
  }
  return STATUS_TO_LOOKUP_FAILURE[status] ?? "error";
};

const RaffleHolderLookupDialogContent = ({ round, onClose }: ContentProps) => {
  const { t } = useResource();
  const { control, handleSubmit } = useForm<RaffleHolderLookupFormData>({
    resolver: zodResolver(getRaffleHolderLookupSchema(t, round.ticketCount)),
  });
  const {
    mutate: lookUpHolder,
    data: holder,
    error,
    isError,
    isPending,
  } = useRaffleTicketHolderLookupMutation();

  const onSubmit = ({ ticketNumber }: RaffleHolderLookupFormData) =>
    lookUpHolder({ roundPublicID: round.publicID, ticketNumber });

  const failureViews: Record<LookupFailure, LookupFailureView> = {
    notFound: { severity: "warning", message: t(RESOURCES.NotFound) },
    sealMismatch: { severity: "error", message: t(RESOURCES.SealMismatch) },
    error: { severity: "error", message: t(RESOURCES.Error) },
  };

  const failureView = isError ? failureViews[getLookupFailure(error)] : null;

  return (
    <>
      <DialogTitle>{`${t(RESOURCES.Title)} — ${round.name}`}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {/* noValidate: zod validates; the browser's number check would block submit untranslated. */}
          <Stack
            component="form"
            noValidate
            gap={1}
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormInput
              control={control}
              name="ticketNumber"
              label={t(RESOURCES.TicketNumber)}
              type="number"
            />
            <Box>
              <Button type="submit" variant="contained" disabled={isPending}>
                {t(RESOURCES.Search)}
              </Button>
            </Box>
          </Stack>

          {failureView && (
            <Alert severity={failureView.severity}>{failureView.message}</Alert>
          )}

          {holder && (
            <Stack gap={0.5}>
              <Typography variant="subtitle2">
                {t(RESOURCES.TicketNumber)}
              </Typography>
              <Typography>{holder.ticketNumber}</Typography>
              <Typography variant="subtitle2">
                {t(RESOURCES.HolderName)}
              </Typography>
              <Typography>{holder.holderName}</Typography>
              <Typography variant="subtitle2">{t(RESOURCES.Email)}</Typography>
              <Typography>{holder.email}</Typography>
              <Typography variant="subtitle2">
                {t(RESOURCES.TicketCount)}
              </Typography>
              <Typography>{holder.ticketCount}</Typography>
              <Typography variant="subtitle2">{t(RESOURCES.Range)}</Typography>
              <Typography>
                {holder.firstTicketNumber}–{holder.lastTicketNumber}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(Resources.Common.Close)}</Button>
      </DialogActions>
    </>
  );
};

/** Resolves a drawn number to its holder on submit only, never while typing (audited). */
export const RaffleHolderLookupDialog = ({ isOpen, round, onClose }: Props) => (
  <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
    {round && (
      <RaffleHolderLookupDialogContent
        key={round.publicID}
        round={round}
        onClose={onClose}
      />
    )}
  </Dialog>
);
